// src/pages/player/PlayerDashboard.jsx
import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {get, ref} from 'firebase/database';
import {database} from '../../services/firebase';
import {useAuth} from '../../contexts/AuthContext';
import Header from '../../components/common/Header';
import PlayerAnnouncements from "../../components/player/PlayerAnnouncement";

export default function PlayerDashboard() {
    const {currentUser} = useAuth();
    const [availableQuizzes, setAvailableQuizzes] = useState([]);
    const [userStats, setUserStats] = useState(null);
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchData() {
            try {
                // Vérifier si le jeu est actif
                const gameStateRef = ref(database, 'gameState');
                const gameStateSnapshot = await get(gameStateRef);
                const gameState = gameStateSnapshot.val() || {active: true, activeQuizzes: {}};

                // Récupérer les quiz disponibles
                const quizzesRef = ref(database, 'quizzes');
                const quizzesSnapshot = await get(quizzesRef);
                const quizzesData = quizzesSnapshot.val() || {};

                // Filtrer les quiz actifs
                const activeQuizzes = [];
                Object.entries(quizzesData).forEach(([quizId, quiz]) => {
                    if (quiz.metadata && quiz.metadata.active && (gameState.active
                        || (gameState.activeQuizzes && gameState.activeQuizzes[quizId]))) {
                        activeQuizzes.push({
                            id: quizId,
                            ...quiz.metadata
                        });
                    }
                });

                setAvailableQuizzes(activeQuizzes);

                // Récupérer les statistiques de l'utilisateur
                if (currentUser) {
                    const userStatsRef = ref(database, `users/${currentUser.uid}/gameHistory`);
                    const userStatsSnapshot = await get(userStatsRef);
                    setUserStats(userStatsSnapshot.val() || {totalGames: 0, totalScore: 0});

                    // Récupérer les statistiques d'entraînement
                    const trainingStatsRef = ref(database, `users/${currentUser.uid}/trainingStats`);
                    const trainingStatsSnapshot = await get(trainingStatsRef);
                    const trainingStats = trainingStatsSnapshot.val() || {
                        questionsAnswered: 0,
                        correctAnswers: 0
                    };

                    setUserStats(prev => ({
                        ...prev,
                        trainingQuestionsAnswered: trainingStats.questionsAnswered,
                        trainingCorrectAnswers: trainingStats.correctAnswers,
                        trainingAccuracy: trainingStats.questionsAnswered > 0
                            ? Math.round((trainingStats.correctAnswers / trainingStats.questionsAnswered) * 100)
                            : 0
                    }));

                    // Récupérer les annonces
                    const announcementsRef = ref(database, 'announcements');
                    const announcementsSnapshot = await get(announcementsRef);
                    const announcementsData = announcementsSnapshot.val() || {};

                    const activeAnnouncements = Object.entries(announcementsData)
                        .filter(([_, announcement]) =>
                            announcement.published &&
                            new Date(announcement.expiresAt) > new Date() &&
                            (announcement.targetAudience === 'all' || announcement.targetAudience === 'players')
                        )
                        .map(([id, data]) => ({
                            id,
                            ...data
                        }))
                        .sort((a, b) =>
                            a.importance === 'important' ? -1 :
                                b.importance === 'important' ? 1 :
                                    new Date(b.publishedAt) - new Date(a.publishedAt)
                        );

                    setAnnouncements(activeAnnouncements);
                }

                setLoading(false);
            } catch (error) {
                console.error("Erreur lors de la récupération des données:", error);
                setLoading(false);
            }
        }

        fetchData();
    }, [currentUser]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    // Composant de compte à rebours
    function CountdownTimer({targetDate}) {
        const [timeLeft, setTimeLeft] = useState({
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0
        });

        useEffect(() => {
            const targetTime = new Date(targetDate).getTime();

            const calculateTimeLeft = () => {
                const now = new Date().getTime();
                const difference = targetTime - now;

                if (difference > 0) {
                    setTimeLeft({
                        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
                        seconds: Math.floor((difference % (1000 * 60)) / 1000)
                    });
                }
            };

            calculateTimeLeft();
            const timer = setInterval(calculateTimeLeft, 1000);

            return () => clearInterval(timer);
        }, [targetDate]);

        return (
            <div className="flex justify-center space-x-4">
                <div className="flex flex-col items-center">
                    <div
                        className="text-3xl font-bold text-indigo-600 bg-indigo-50 w-16 h-16 flex items-center justify-center rounded-lg">
                        {timeLeft.days}
                    </div>
                    <span className="text-xs mt-1 text-gray-500">Jours</span>
                </div>
                <div className="flex flex-col items-center">
                    <div
                        className="text-3xl font-bold text-indigo-600 bg-indigo-50 w-16 h-16 flex items-center justify-center rounded-lg">
                        {timeLeft.hours.toString().padStart(2, '0')}
                    </div>
                    <span className="text-xs mt-1 text-gray-500">Heures</span>
                </div>
                <div className="flex flex-col items-center">
                    <div
                        className="text-3xl font-bold text-indigo-600 bg-indigo-50 w-16 h-16 flex items-center justify-center rounded-lg">
                        {timeLeft.minutes.toString().padStart(2, '0')}
                    </div>
                    <span className="text-xs mt-1 text-gray-500">Minutes</span>
                </div>
                <div className="flex flex-col items-center">
                    <div
                        className="text-3xl font-bold text-indigo-600 bg-indigo-50 w-16 h-16 flex items-center justify-center rounded-lg">
                        {timeLeft.seconds.toString().padStart(2, '0')}
                    </div>
                    <span className="text-xs mt-1 text-gray-500">Secondes</span>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-b from-indigo-50 to-purple-100 min-h-screen">
            <Header title="BibleQuiz"/>

            <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <PlayerAnnouncements/>
                {/* Statistiques */}
                <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                                    </svg>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Total de Quiz</dt>
                                        <dd className="flex items-baseline">
                                            <div className="text-2xl font-semibold text-gray-900">
                                                {userStats?.totalGames || 0}
                                            </div>
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                    </svg>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Score Total</dt>
                                        <dd className="flex items-baseline">
                                            <div className="text-2xl font-semibold text-gray-900">
                                                {userStats?.totalScore || 0}
                                            </div>
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                                    </svg>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Précision
                                            (Entraînement)
                                        </dt>
                                        <dd className="flex items-baseline">
                                            <div className="text-2xl font-semibold text-gray-900">
                                                {userStats?.trainingAccuracy || 0}%
                                            </div>
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Annonces */}
                {announcements.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Annonces</h2>
                        <div className="bg-white shadow overflow-hidden sm:rounded-md">
                            <ul className="divide-y divide-gray-200">
                                {announcements.map((announcement) => (
                                    <li key={announcement.id}>
                                        <div
                                            className={`px-4 py-4 sm:px-6 ${announcement.importance === 'important' ? 'bg-yellow-50' : ''}`}>
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm font-medium text-indigo-600 truncate">{announcement.title}</p>
                                                <div className="ml-2 flex-shrink-0 flex">
                                                    {announcement.importance === 'important' && (
                                                        <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                            Important
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mt-2 sm:flex sm:justify-between">
                                                <div className="sm:flex">
                                                    <p className="text-sm text-gray-500">
                                                        {announcement.content}
                                                    </p>
                                                </div>
                                                <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                                    <p>
                                                        Publié
                                                        le {new Date(announcement.publishedAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {/* Quiz disponibles */}
                <h2 className="text-lg font-medium text-gray-900 mb-4">Quiz disponibles</h2>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {availableQuizzes.length > 0 ? (
                        availableQuizzes.map((quiz) => (
                            <div key={quiz.id}
                                 className="bg-white overflow-hidden shadow rounded-lg transition transform hover:scale-105">
                                <div className="px-4 py-5 sm:p-6">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900">{quiz.title}</h3>
                                    <div className="mt-2 max-w-xl text-sm text-gray-500">
                                        <p>{quiz.description}</p>
                                    </div>
                                    <div className="mt-3 flex items-center">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                        quiz.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                            quiz.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                    }`}>
                      {quiz.difficulty === 'easy' ? 'Facile' :
                          quiz.difficulty === 'medium' ? 'Moyen' : 'Difficile'}
                    </span>
                                        <span className="ml-2 text-xs text-gray-500">
                      {quiz.timePerQuestion} sec/question
                    </span>
                                        <span className="ml-2 text-xs text-gray-500">
                      {quiz.totalQuestions} questions
                    </span>
                                    </div>
                                    <div className="mt-5">
                                        <Link
                                            to={`/quiz/${quiz.id}`}
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            Commencer
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-full text-center py-12 bg-white rounded-lg shadow-sm">
                            <svg className="mx-auto h-16 w-16 text-indigo-500" fill="none" viewBox="0 0 24 24"
                                 stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>

                            <h3 className="mt-4 text-xl font-bold text-gray-900">Concours BibleQuiz en préparation</h3>

                            <p className="mt-2 text-sm text-gray-600">
                                Les quiz seront activés pour la compétition "Les Cracks" de la JEESIM
                            </p>

                            {/* Compte à rebours */}
                            <div className="mt-6">
                                <CountdownTimer targetDate="2025-04-10T20:05:00"/>
                            </div>

                            <div className="mt-6 max-w-md mx-auto">
                                <p className="text-sm text-gray-500">
                                    Profitez de ce temps pour vous préparer! Explorez la Bible et révisez vos
                                    connaissances.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Mode entraînement */}
                <div className="mt-10">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Mode Entraînement</h2>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                Pratiquez vos connaissances bibliques
                            </h3>
                            <div className="mt-2 max-w-xl text-sm text-gray-500">
                                <p>
                                    Créez une session d'entraînement personnalisée avec des questions aléatoires.
                                    Sélectionnez le nombre de questions, les catégories et la difficulté.
                                </p>
                            </div>
                            <div className="mt-5">
                                <Link
                                    to="/training"
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Créer une session d'entraînement
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
